import React, { useEffect, useState } from 'react';
import { apiService } from '../services/api';
import {
  BuildingOffice2Icon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import EmptyState from '../components/ui/EmptyState';

interface Contractor {
  id: string;
  name: string;
  status: string;
  rating: number | null;
}

interface ServiceContract {
  id: string;
  contractor_id: string;
  name: string;
  response_minutes: number;
  resolution_minutes: number;
  breach_threshold: number;
  breach_window_days: number;
  status: string;
  start_date: string;
  end_date: string | null;
}

interface Breach {
  id: string;
  breach_type: string;
  due_at: string;
  detected_at: string;
  report_title?: string;
  report_number?: string;
}

interface Scorecard {
  contractor: Contractor;
  contracts: ServiceContract[];
  totalAssignments: number;
  breachCount: number;
  breachRate: number;
  recentBreaches: Breach[];
  openEscalations: Escalation[];
}

interface Escalation {
  id: string;
  contractor_id: string;
  contractor_name?: string;
  breach_count: number;
  reason: string;
  status: string;
  escalation_type?: 'breach_threshold' | 'unstaffed_category';
  created_at: string;
}

/**
 * Municipality admin / support-staff view of technician-supplying
 * contractors: their agreed SLA terms, how often they've breached them,
 * and the auto-escalation queue raised when a contractor crosses its
 * contract's breach threshold. Distinct from ContractorPortal.tsx, which
 * is the contractor's own self-service work-order view — this page is
 * the municipality's side of the same relationship.
 */
const ContractorPerformance: React.FC = () => {
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [selectedContractorId, setSelectedContractorId] = useState<string>('');
  const [scorecard, setScorecard] = useState<Scorecard | null>(null);
  const [escalations, setEscalations] = useState<Escalation[]>([]);
  const [loading, setLoading] = useState(true);
  const [scorecardLoading, setScorecardLoading] = useState(false);
  const [showNewContract, setShowNewContract] = useState(false);
  const [form, setForm] = useState({
    name: '', responseMinutes: '240', resolutionMinutes: '2880',
    breachThreshold: '3', breachWindowDays: '30', startDate: new Date().toISOString().slice(0, 10),
  });
  const [error, setError] = useState('');

  const loadContractors = async () => {
    setLoading(true);
    const res = await apiService.get<Contractor[]>('/platform/contractors');
    if (res.success && res.data) {
      setContractors(res.data);
      if (res.data.length > 0 && !selectedContractorId) setSelectedContractorId(res.data[0].id);
    }
    setLoading(false);
  };

  const loadEscalations = async () => {
    const res = await apiService.get<Escalation[]>('/contracts/escalations/queue?status=open');
    if (res.success && res.data) setEscalations(res.data);
  };

  const loadScorecard = async (contractorId: string) => {
    if (!contractorId) return;
    setScorecardLoading(true);
    const res = await apiService.get<Scorecard>(`/contracts/scorecard/${contractorId}`);
    if (res.success && res.data) setScorecard(res.data);
    setScorecardLoading(false);
  };

  useEffect(() => {
    loadContractors();
    loadEscalations();
  }, []);

  useEffect(() => {
    if (selectedContractorId) loadScorecard(selectedContractorId);
  }, [selectedContractorId]);

  const handleCreateContract = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!selectedContractorId) {
      setError('Select a contractor first');
      return;
    }
    const res = await apiService.post('/contracts', {
      contractorId: selectedContractorId,
      name: form.name,
      responseMinutes: Number(form.responseMinutes),
      resolutionMinutes: Number(form.resolutionMinutes),
      breachThreshold: Number(form.breachThreshold),
      breachWindowDays: Number(form.breachWindowDays),
      startDate: form.startDate,
    });
    if (res.success) {
      setShowNewContract(false);
      setForm({ name: '', responseMinutes: '240', resolutionMinutes: '2880', breachThreshold: '3', breachWindowDays: '30', startDate: new Date().toISOString().slice(0, 10) });
      loadScorecard(selectedContractorId);
    } else {
      setError(res.error?.message || 'Failed to create contract');
    }
  };

  const resolveEscalation = async (id: string) => {
    const notes = window.prompt('Resolution notes (optional):') || '';
    const res = await apiService.patch(`/contracts/escalations/${id}`, { status: 'resolved', resolutionNotes: notes });
    if (res.success) {
      loadEscalations();
      if (selectedContractorId) loadScorecard(selectedContractorId);
    }
  };

  const acknowledgeEscalation = async (id: string) => {
    const res = await apiService.patch(`/contracts/escalations/${id}`, { status: 'acknowledged' });
    if (res.success) loadEscalations();
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <BuildingOffice2Icon className="h-6 w-6 text-gray-400" />
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Contractor Performance</h1>
      </div>

      {/* Auto-escalation queue — raised automatically when a contractor
          crosses its contract's breach threshold. */}
      {escalations.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center gap-2 text-red-800 font-medium">
            <ExclamationTriangleIcon className="h-5 w-5" />
            Open performance escalations ({escalations.length})
          </div>
          {escalations.map((e) => (
            <div key={e.id} className="bg-white rounded-md border border-red-100 p-3 flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <div className="font-medium text-gray-900">{e.contractor_name || 'Contractor'}</div>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${e.escalation_type === 'unstaffed_category' ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-700'}`}>
                    {e.escalation_type === 'unstaffed_category' ? 'No technician available' : 'SLA breach threshold'}
                  </span>
                </div>
                <div className="text-sm text-gray-600">{e.reason}</div>
                <div className="text-xs text-gray-400 mt-1">Raised {new Date(e.created_at).toLocaleString()}</div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={() => acknowledgeEscalation(e.id)}
                  className="text-xs px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Acknowledge
                </button>
                <button
                  onClick={() => resolveEscalation(e.id)}
                  className="text-xs px-3 py-1.5 rounded-md bg-red-600 text-white hover:bg-red-700"
                >
                  Resolve
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white shadow rounded-lg p-6">
        {loading ? (
          <p className="text-sm text-gray-500">Loading contractors…</p>
        ) : contractors.length === 0 ? (
          <EmptyState
            icon={BuildingOffice2Icon}
            title="No contractors on file yet"
            description="Add a contractor to start tracking performance and contract compliance."
          />
        ) : (
          <>
            <div className="flex items-center gap-3 mb-4">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Contractor</label>
              <select
                value={selectedContractorId}
                onChange={(e) => setSelectedContractorId(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-1.5 text-sm"
              >
                {contractors.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <button
                onClick={() => setShowNewContract((s) => !s)}
                className="ml-auto flex items-center gap-1 text-sm px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700"
              >
                <PlusIcon className="h-4 w-4" /> New contract
              </button>
            </div>

            {showNewContract && (
              <form onSubmit={handleCreateContract} className="mb-6 border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50">
                {error && <div className="text-sm text-red-600">{error}</div>}
                <div className="grid grid-cols-2 gap-3">
                  <input required placeholder="Contract name" value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="border border-gray-300 rounded-md px-3 py-2 text-sm col-span-2" />
                  <label className="text-xs text-gray-600 dark:text-gray-400 col-span-1">
                    Response target (minutes)
                    <input type="number" required value={form.responseMinutes}
                      onChange={(e) => setForm({ ...form, responseMinutes: e.target.value })}
                      className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                  </label>
                  <label className="text-xs text-gray-600 dark:text-gray-400 col-span-1">
                    Resolution target (minutes)
                    <input type="number" required value={form.resolutionMinutes}
                      onChange={(e) => setForm({ ...form, resolutionMinutes: e.target.value })}
                      className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                  </label>
                  <label className="text-xs text-gray-600 dark:text-gray-400 col-span-1">
                    Breach threshold (count)
                    <input type="number" required value={form.breachThreshold}
                      onChange={(e) => setForm({ ...form, breachThreshold: e.target.value })}
                      className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                  </label>
                  <label className="text-xs text-gray-600 dark:text-gray-400 col-span-1">
                    Breach window (days)
                    <input type="number" required value={form.breachWindowDays}
                      onChange={(e) => setForm({ ...form, breachWindowDays: e.target.value })}
                      className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                  </label>
                  <label className="text-xs text-gray-600 dark:text-gray-400 col-span-2">
                    Start date
                    <input type="date" required value={form.startDate}
                      onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                      className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                  </label>
                </div>
                <p className="text-xs text-gray-500">
                  Once a contractor breaches its resolution target this many times within the window, an escalation is raised automatically.
                </p>
                <button type="submit" className="text-sm px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700">
                  Create contract
                </button>
              </form>
            )}

            {scorecardLoading ? (
              <p className="text-sm text-gray-500">Loading scorecard…</p>
            ) : scorecard ? (
              <div className="space-y-6">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-xs text-gray-500 dark:text-gray-400">Rating</div>
                    <div className="text-xl font-semibold text-gray-900 dark:text-gray-100">{scorecard.contractor.rating ?? '—'}</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-xs text-gray-500 dark:text-gray-400">Total jobs</div>
                    <div className="text-xl font-semibold text-gray-900 dark:text-gray-100">{scorecard.totalAssignments}</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-xs text-gray-500 dark:text-gray-400">Breaches</div>
                    <div className="text-xl font-semibold text-red-600">{scorecard.breachCount}</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-xs text-gray-500 dark:text-gray-400">Breach rate</div>
                    <div className="text-xl font-semibold text-gray-900 dark:text-gray-100">{(scorecard.breachRate * 100).toFixed(1)}%</div>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Active contracts</h3>
                  {scorecard.contracts.length === 0 ? (
                    <p className="text-sm text-gray-500">No contracts on file for this contractor yet — add one above.</p>
                  ) : (
                    <div className="space-y-2">
                      {scorecard.contracts.map((c) => (
                        <div key={c.id} className="flex items-center justify-between border border-gray-200 rounded-md px-3 py-2 text-sm">
                          <div>
                            <span className="font-medium">{c.name}</span>
                            <span className="text-gray-500 ml-2">
                              response {c.response_minutes}m · resolution {c.resolution_minutes}m · escalate after {c.breach_threshold} breaches / {c.breach_window_days}d
                            </span>
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${c.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                            {c.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Recent breaches</h3>
                  {scorecard.recentBreaches.length === 0 ? (
                    <p className="text-sm text-gray-500 flex items-center gap-1"><CheckCircleIcon className="h-4 w-4 text-green-500" /> No breaches recorded.</p>
                  ) : (
                    <div className="space-y-1">
                      {scorecard.recentBreaches.map((b) => (
                        <div key={b.id} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                          <ClockIcon className="h-4 w-4 text-red-400" />
                          <span className="font-medium capitalize">{b.breach_type}</span> — {b.report_title || b.report_number || 'Report'} — due {new Date(b.due_at).toLocaleString()}, detected {new Date(b.detected_at).toLocaleString()}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

export default ContractorPerformance;
